/**
 * CommunityChatMessageRemapper
 *
 * Round-trips a `community_messages` Firestore document between its wire JSON
 * and a CommunityChatMessageDto. This is the only entry point from raw JSON to
 * the DTO (the data layer never imports a remapper — the domain service bridges
 * data → domain).
 *
 * Flutter source: lib/data/models/community_chat_mssage.dart:9-34
 *   (+ generated community_chat_mssage.g.dart:9-49).
 *
 * `CommunityChatMessage` has NO `@JsonKey` overrides — every Dart property name
 * equals its Firestore field (`parentId`, `createdTimestamp`, `updatedTimestamp`,
 * `messageUsersInfo`, `communityReply`, `mediaInfo`, `broadcastingInfo` are the
 * literal Firestore field names, generated code does not translate keys).
 *
 * Defaults applied by `fromJson` (defensive against partial documents):
 *   - `id` / `text` `?? ''`, `likes` / `createdTimestamp` / `type` `?? 0`
 *   - `success` `?? true` (Flutter `bool success = true`,
 *     community_chat_mssage.dart:18)
 *
 * Nested objects are delegated to their canonical remappers so default
 * application is uniform: `communityReply` → CommunityReplyRemapper,
 * `messageUsersInfo` → MessageUsersInfoRemapper, `mediaInfo` →
 * MessageMediaInfoRemapper, `broadcastingInfo` →
 * MessageBroadcastingInfoRemapper.
 *
 * `broadcastingInfo` is the message-level broadcasting struct (present on
 * `type = live` messages), delegated to `MessageBroadcastingInfoRemapper` in all
 * four directions like the other nested objects. Its `latestLiveGiftInfo`
 * sub-field is typed as a `LatestLiveGiftInfoEntity` on the entity (Task 1); the
 * DTO keeps the opaque `JsonObject` for the wire round-trip and the remapper
 * bridges the two at the DTO↔entity hop.
 *
 * Flutter's `parent` field (`includeToJson:false / includeFromJson:false`,
 * community_chat_mssage.dart:26-27) is not part of the wire shape and is not
 * handled here.
 */
import type { CommunityChatMessageDto } from '@/data/dtos/community/communityChatMessageDto';
import type { JsonObject } from '@/data/types/json';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { MessageMediaInfoEntity } from '@/domain/entities/info/messageMediaInfoEntity';
import type { AddCommunityMessageInput } from '@/domain/input/community/addCommunityMessageInput';
import type { AddLiveMessageInput } from '@/domain/input/liveStreaming/addLiveMessageInput';
import { CommunityReplyRemapper } from '@/domain/remappers/community/communityReplyRemapper';
import { MessageUsersInfoRemapper } from '@/domain/remappers/info/messageUsersInfoRemapper';
import { MessageMediaInfoRemapper } from '@/domain/remappers/info/messageMediaInfoRemapper';
import { MessageBroadcastingInfoRemapper } from '@/domain/remappers/info/messageBroadcastingInfoRemapper';
import {
  buildCommunityReplyPreview,
  isCommunityMediaMessageType,
} from '@/domain/utils/communityUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';

export class CommunityChatMessageRemapper {
  static fromJson(data: JsonObject): CommunityChatMessageDto {
    const communityReplyRaw = data['communityReply'] as JsonObject | undefined | null;
    const messageUsersInfoRaw = data['messageUsersInfo'] as JsonObject | undefined | null;
    const mediaInfoRaw = data['mediaInfo'] as JsonObject | undefined | null;
    const broadcastingInfoRaw = data['broadcastingInfo'] as JsonObject | undefined | null;

    const dto: CommunityChatMessageDto = {
      id: (data['id'] as string | undefined) ?? '',
      text: (data['text'] as string | undefined) ?? '',
      likes: (data['likes'] as number | undefined) ?? 0,
      createdTimestamp: (data['createdTimestamp'] as number | undefined) ?? 0,
      type: (data['type'] as number | undefined) ?? 0,
      // Flutter default: `bool success = true` (community_chat_mssage.dart:18).
      success: (data['success'] as boolean | undefined) ?? true,
      // `messageUsersInfo` is `late` (required); delegate to its remapper, which
      // applies its own `?? ''` defaults if the nested object is absent.
      messageUsersInfo: MessageUsersInfoRemapper.fromJson(messageUsersInfoRaw ?? {}),
    };

    if (data['parentId'] !== undefined) {
      dto.parentId = data['parentId'] as string | undefined;
    }
    if (data['updatedTimestamp'] !== undefined) {
      dto.updatedTimestamp = data['updatedTimestamp'] as number | undefined;
    }
    // Nested objects: parse only when the source value is non-null (matches
    // Flutter's `json['communityReply'] == null ? null : …`).
    if (communityReplyRaw) {
      dto.communityReply = CommunityReplyRemapper.fromJson(communityReplyRaw);
    }
    if (mediaInfoRaw) {
      dto.mediaInfo = MessageMediaInfoRemapper.fromJson(mediaInfoRaw);
    }
    if (broadcastingInfoRaw) {
      dto.broadcastingInfo = MessageBroadcastingInfoRemapper.fromJson(broadcastingInfoRaw);
    }

    return dto;
  }

  static fromJsonList(dataList: JsonObject[]): CommunityChatMessageDto[] {
    return dataList.map(CommunityChatMessageRemapper.fromJson);
  }

  static toJson(dto: CommunityChatMessageDto): JsonObject {
    const json: JsonObject = {
      id: dto.id,
      text: dto.text,
      likes: dto.likes,
      createdTimestamp: dto.createdTimestamp,
      type: dto.type,
      success: dto.success,
      messageUsersInfo: MessageUsersInfoRemapper.toJson(dto.messageUsersInfo),
    };

    if (dto.parentId !== undefined) json.parentId = dto.parentId;
    if (dto.updatedTimestamp !== undefined) json.updatedTimestamp = dto.updatedTimestamp;
    if (dto.communityReply !== undefined) {
      json.communityReply = CommunityReplyRemapper.toJson(dto.communityReply);
    }
    if (dto.mediaInfo !== undefined) {
      json.mediaInfo = MessageMediaInfoRemapper.toJson(dto.mediaInfo);
    }
    if (dto.broadcastingInfo !== undefined) {
      json.broadcastingInfo = MessageBroadcastingInfoRemapper.toJson(dto.broadcastingInfo);
    }

    return json;
  }

  /**
   * Map a `CommunityChatMessageDto` to its domain entity. Nested objects are
   * delegated to their canonical remappers (`CommunityReplyRemapper`,
   * `MessageUsersInfoRemapper`, `MessageMediaInfoRemapper`) so the absolute-URL
   * derivation is uniform — matching how the JSON->DTO direction already
   * delegates. The transient `isUploading` convenience mirrors
   * `mediaInfo.isUploading` for the renderer.
   */
  static toEntity(dto: CommunityChatMessageDto): CommunityChatMessageEntity {
    const mediaInfo = dto.mediaInfo
      ? MessageMediaInfoRemapper.toEntity(dto.mediaInfo)
      : undefined;

    return {
      id: dto.id,
      parentId: dto.parentId,
      text: dto.text,
      likes: dto.likes,
      createdTimestamp: dto.createdTimestamp,
      updatedTimestamp: dto.updatedTimestamp,
      type: dto.type,
      success: dto.success,
      communityReply: dto.communityReply
        ? CommunityReplyRemapper.toEntity(dto.communityReply)
        : undefined,
      messageUsersInfo: MessageUsersInfoRemapper.toEntity(dto.messageUsersInfo),
      mediaInfo,
      broadcastingInfo: dto.broadcastingInfo
        ? MessageBroadcastingInfoRemapper.fromDto(dto.broadcastingInfo)
        : undefined,
      // Transient renderer convenience — mirrors `mediaInfo.isUploading`.
      isUploading: mediaInfo?.isUploading,
    };
  }

  /**
   * Map a `CommunityChatMessageEntity` back to its DTO for serialization. The
   * entity carries both relative and absolute media/reply URLs; only the
   * RELATIVE values are persisted (the absolute getters are derived, never
   * stored — parity with Flutter where `get path` is a computed getter and only
   * `relativePath` is serialized via `@JsonKey(name: 'path')`). Named
   * `toDtoFromEntity` (not `toDto`) to avoid colliding with the JSON-emitting
   * `toJson`.
   */
  static toDtoFromEntity(entity: CommunityChatMessageEntity): CommunityChatMessageDto {
    const dto: CommunityChatMessageDto = {
      id: entity.id,
      text: entity.text,
      likes: entity.likes,
      createdTimestamp: entity.createdTimestamp,
      type: entity.type,
      success: entity.success,
      messageUsersInfo: MessageUsersInfoRemapper.toDtoFromEntity(entity.messageUsersInfo),
    };

    if (entity.parentId !== undefined) dto.parentId = entity.parentId;
    if (entity.updatedTimestamp !== undefined) dto.updatedTimestamp = entity.updatedTimestamp;
    if (entity.communityReply !== undefined) {
      dto.communityReply = CommunityReplyRemapper.toDtoFromEntity(entity.communityReply);
    }
    if (entity.mediaInfo !== undefined) {
      dto.mediaInfo = MessageMediaInfoRemapper.toDtoFromEntity(entity.mediaInfo);
    }
    if (entity.broadcastingInfo !== undefined) {
      dto.broadcastingInfo = MessageBroadcastingInfoRemapper.toDtoFromEntity(entity.broadcastingInfo);
    }

    return dto;
  }

  /**
   * Build the optimistic `CommunityChatMessageEntity` for a new community-wall
   * message from an `AddCommunityMessageInput`. SYNCHRONOUS, no I/O — the hook
   * calls this, inserts the result at index 0 (and clears the reply / scrolls to
   * top) BEFORE awaiting the Firestore write, mirroring Flutter's "add message in
   * list before saving to DB" ordering (community_screen.dart:532-548). The
   * message-build logic lives here (NOT inlined in the hook) so the optimistic
   * entity construction stays a single source of truth.
   *
   * Two branches selected by `input.media`:
   *
   * TEXT branch (`input.media` absent) —
   *   Flutter: lib/presentation/screens/community/community_screen.dart:459-550
   *   (`addMessage`, text branch).
   *   - `community_screen.dart:476` — `timestamp = DateTime.now().millisecondsSinceEpoch`
   *     stamped at SEND time; the web keeps its `Date.now()`-at-call stamp.
   *   - `community_screen.dart:520-530` — the message: `id = Uuid().v1()`,
   *     `text`, `communityReply = replyInfo`, `likes = 0`, `createdTimestamp =
   *     timestamp`, `type = 0` (text), `parentId = messageToReply?.id`.
   *   - Empty-text guarding is NOT done here: the composer/hook already gates on
   *     non-empty trimmed input (community_screen.dart:472-474).
   *
   * MEDIA branch (`input.media` present; image only, video deferred) —
   *   Flutter: community_screen.dart:398-451 (`_addMediaMessage`) +
   *   medias_repository.dart:273-277 (type / aspectRatio resolution).
   *   - `type = input.media.type`. Flutter builds `type = -1` at :448-450 and
   *     resolves it to `image`/`video` just before the doc write
   *     (medias_repository.dart:273-276); the web builds the terminal value
   *     directly since the sheet already knows the media type — the persisted
   *     value is identical and `-1` never reaches Firestore in either client.
   *   - `createdTimestamp = input.media.capturedAtMillis` — the TAP-time snapshot
   *     (community_screen.dart:399 `timestamp = DateTime.now()...` → assigned
   *     :446), NOT the confirm/send time, so the wall sort position matches
   *     Flutter for messages that arrive while the add-content sheet is open.
   *   - `mediaInfo = { isUploading: true, aspectRatio: input.media.aspectRatio }`.
   *     `relativePath`/`relativeThumbnailPath` (and their `path`/`thumbnail`
   *     absolute getters) are left `undefined` — the transcode CF fills them
   *     (transcode_community_message_image.ts:169-181). Flutter also leaves them
   *     null on the optimistic build (message_media_info.dart:6-34, task-prompt
   *     line 15).
   *   - There is NO trim/empty guard on the media path (unlike text
   *     :472-474), so `text` is carried verbatim including empty string.
   *   - Flutter mints the message `id` at tap (:442); no threading is needed —
   *     a random UUID has identical semantics whenever it is minted.
   *
   * Serialization note (do NOT change `MessageMediaInfoRemapper`): Flutter's
   * generated `toJson` serializes explicit `path: null` / `thumbnail: null` on
   * the optimistic doc (json_serializable default `includeIfNull: true`), while
   * the web's `MessageMediaInfoRemapper.toJson` omits `undefined` keys
   * (messageMediaInfoRemapper.ts:52-64). These are functionally equivalent for
   * both readers (both `fromJson` paths null-default the missing keys) and are
   * reconciled by the CF's full-map merge write, which writes all four keys
   * (transcode_community_message_image.ts:169-181).
   *
   * Shared across both branches:
   *   - `community_screen.dart:514-518` / `:434-438` — `messageUsersInfo`:
   *     `owner = widget.user.firebaseId` (the WALL OWNER, the creator — NOT the
   *     sender), `userId = getCurrentFirebaseUser()!.uid` (the sender's firebase
   *     uid), `userName = _currentUser.name` (the sender's name).
   *   - Reply preview is built when `replyToMessage` is present, for BOTH text
   *     and media (image/video) reply targets — `buildCommunityReplyPreview`
   *     carries the media descriptor (community_screen.dart:493-511 / :415-431).
   *     Only the reply-preview *rendering* stays deferred (marker at
   *     CommunityMessageReplyPreview.tsx:85). When absent, `communityReply` and
   *     `parentId` stay unset.
   *
   * Media-reply `mediaInfo` copy (TEXT send only) — community_screen.dart:510 +
   * :525: on a TEXT send replying to a media (image/video) message, the built
   * message's own `mediaInfo` is set to a copy of the reply target's `mediaInfo`.
   * This matters cross-client: Flutter's reply-media renderer null-asserts
   * `message.mediaInfo!` on the text message (community_message_item.dart:352-384),
   * so a web-authored doc missing the copy would crash the Flutter renderer. On a
   * MEDIA send `_addMediaMessage` never copies (community_screen.dart:450) — the
   * fresh `{ isUploading: true, aspectRatio }` struct wins.
   *
   * Documented divergence from the chat send path: unlike the chat flow, the
   * community build (community_screen.dart) sets no `success` field (the model
   * default `success = true`, community_chat_mssage.dart) and never mutates the
   * in-list message's `success` on a failed write. We mirror that: the built
   * entity always carries `success: true` regardless of the write outcome.
   *
   * @returns The freshly built `CommunityChatMessageEntity`. Its `success` is
   *   always `true` — see the documented divergence above.
   */
  static fromInput(input: AddCommunityMessageInput): CommunityChatMessageEntity {
    const { owner, text, currentUser, replyToMessage, media } = input;

    // Reply preview is built whenever `replyToMessage` is present — for both
    // text and media (image/video) reply targets (buildCommunityReplyPreview
    // carries the media descriptor). When `replyToMessage` is absent,
    // `communityReply` is left undefined and `parentId` stays unset
    // (community_screen.dart:478, :524, :530 with a null `messageToReply`).
    const communityReply =
      replyToMessage !== undefined
        ? buildCommunityReplyPreview(replyToMessage)
        : undefined;

    // `mediaInfo` resolution:
    //   - MEDIA send (`media` present): the fresh uploading struct wins —
    //     `_addMediaMessage` never copies the reply target's media
    //     (community_screen.dart:450). `relativePath`/`relativeThumbnailPath`
    //     (+ derived absolutes) stay undefined; the CF fills them
    //     (medias_repository.dart:277 + transcode_community_message_image.ts).
    //   - TEXT send (`media` absent) replying to a media message: copy the reply
    //     target's `mediaInfo` onto the built message so Flutter's null-asserting
    //     reply-media renderer does not crash on a web-authored doc
    //     (community_screen.dart:510 + :525; community_message_item.dart:352-384).
    //   - otherwise: no `mediaInfo`.
    let mediaInfo: MessageMediaInfoEntity | undefined;
    if (media !== undefined) {
      mediaInfo = { isUploading: true, aspectRatio: media.aspectRatio };
    } else if (
      replyToMessage !== undefined &&
      isCommunityMediaMessageType(replyToMessage.type) &&
      replyToMessage.mediaInfo !== undefined
    ) {
      // Shallow copy — parity with Flutter's `mediaInfo = messageToReply.mediaInfo!`
      // reference assignment (community_screen.dart:510), without aliasing the
      // reply target's object. The source's `isUploading` (false on an uploaded
      // image) is preserved verbatim.
      mediaInfo = { ...replyToMessage.mediaInfo };
    }

    return {
      // `Uuid().v1()` -> `generateUuidV1()` (community_screen.dart:522 / :442).
      id: generateUuidV1(),
      text,
      likes: 0,
      // TEXT: `DateTime.now().millisecondsSinceEpoch` at send time
      // (community_screen.dart:476). MEDIA: the tap-time snapshot
      // (community_screen.dart:399 → :446).
      createdTimestamp: media !== undefined ? media.capturedAtMillis : Date.now(),
      // TEXT: `type = 0` (text). MEDIA: the terminal media type (image) — see
      // the `-1`-resolution note in the JSDoc (community_screen.dart:448-450 +
      // medias_repository.dart:273-276).
      type: media !== undefined ? media.type : CommunityChatMessageType.text,
      // Flutter's `CommunityChatMessage` model defaults `success = true` and the
      // build never sets it — see the documented divergence. The optimistic
      // entity carries `success: true` regardless of the write.
      success: true,
      messageUsersInfo: {
        // owner = the WALL OWNER (creator), NOT the sender —
        // community_screen.dart:516 / :436.
        owner,
        // userId = the sender's firebase uid (== the user's firebaseId) —
        // community_screen.dart:517 / :437.
        userId: currentUser.firebaseId,
        // userName = the sender's name — community_screen.dart:518 / :438.
        userName: currentUser.name,
      },
      communityReply,
      // MEDIA send: the fresh uploading struct. TEXT send replying to media: a
      // copy of the reply target's mediaInfo. Otherwise omitted — see the
      // resolution block above. The top-level `isUploading` mirror is kept in
      // sync with `mediaInfo.isUploading` so `fromInput`-built optimistic
      // entities honour the same invariant `toEntity` maintains
      // (communityChatMessageRemapper.ts:151; communityChatMessageEntity.ts:44).
      ...(mediaInfo !== undefined
        ? { mediaInfo, isUploading: mediaInfo.isUploading }
        : {}),
      // parentId = messageToReply?.id (community_screen.dart:530 / :451).
      parentId: replyToMessage?.id,
    };
  }

  /**
   * Build the optimistic `CommunityChatMessageEntity` for a new LIVE-session
   * chat message from an `AddLiveMessageInput`. SYNCHRONOUS, no I/O — the Task-7
   * hook calls this, inserts the result at index 0 (and clears the input /
   * scrolls to top) BEFORE awaiting the Firestore write, mirroring Flutter's
   * "add message in list before saving to DB" ordering
   * (live_streaming_screen.dart:799). The message build lives here (NOT inlined
   * in the hook) so the optimistic entity construction stays a single source of
   * truth, exactly like the sibling `fromInput` wall build.
   *
   * Flutter source: lib/presentation/screens/community/live_streaming_screen.dart:783-797
   *   (`addLiveMessage` message build).
   *   - `id = Uuid().v1()` (:791) -> `generateUuidV1()`, the same helper
   *     `fromInput` uses (:320).
   *   - `text = _textEditingController.text` (:792) — carried verbatim (the
   *     empty-text guard is on the composer/hook, live_streaming_screen.dart:774).
   *   - `likes = 0` (:793); `createdTimestamp = timestamp` where
   *     `timestamp = DateTime.now().millisecondsSinceEpoch` (:777, :794) — the
   *     web keeps its `Date.now()`-at-call stamp.
   *   - `type = 0` (text, :796) — a live chat send is ALWAYS a text message
   *     (`CommunityChatMessageType.text`); no `image`/`video`/`gift`/`live`.
   *   - `messageUsersInfo` (:783-787): `owner = widget.broadcasterFirebaseId`
   *     (the BROADCASTER, not the wall owner), `userId = currentUser.firebaseId`,
   *     `userName = currentUser.name`.
   *   - `parentId = widget.liveStreamingMessageId` (:797) — the live SESSION id,
   *     NEVER a reply id. Unlike `fromInput` (where `parentId = replyToMessage?.id`)
   *     this addresses the `live_messages` sub-collection, so
   *     `LiveStreamingService.addLiveMessage` (Task 2) writes to
   *     `.../community_messages/{parentId}/live_messages/{id}` off `owner` +
   *     `parentId`.
   *   - NO `communityReply`, NO `mediaInfo`, NO `broadcastingInfo` — a live send
   *     has no reply / media / gift surfaces.
   *
   * `success` is set `true` to match Flutter's `CommunityChatMessage` model
   * default (`bool success = true`, community_chat_mssage.dart:18); the build
   * never sets it, and Flutter's failure path only re-renders (it leaves the
   * failed message in the list) — the web's by-id rollback is realized in the
   * Task-7 hook, not here.
   *
   * @returns The freshly built text `CommunityChatMessageEntity`.
   */
  static fromLiveInput(input: AddLiveMessageInput): CommunityChatMessageEntity {
    const { liveStreamingMessageId, broadcasterFirebaseId, text, currentUser } = input;

    return {
      // `Uuid().v1()` -> `generateUuidV1()` (live_streaming_screen.dart:791).
      id: generateUuidV1(),
      // text = _textEditingController.text, verbatim (live_streaming_screen.dart:792).
      text,
      // likes = 0 (live_streaming_screen.dart:793).
      likes: 0,
      // createdTimestamp = DateTime.now().millisecondsSinceEpoch at send time
      // (live_streaming_screen.dart:777 -> :794).
      createdTimestamp: Date.now(),
      // type = 0 (text) — a live chat send is always text (live_streaming_screen.dart:796).
      type: CommunityChatMessageType.text,
      // Model default `success = true` (community_chat_mssage.dart:18); see JSDoc.
      success: true,
      messageUsersInfo: {
        // owner = the BROADCASTER, NOT the wall owner (live_streaming_screen.dart:785).
        owner: broadcasterFirebaseId,
        // userId = the sender's firebase uid (== firebaseId) (live_streaming_screen.dart:786).
        userId: currentUser.firebaseId,
        // userName = the sender's name (live_streaming_screen.dart:787).
        userName: currentUser.name,
      },
      // parentId = the live SESSION id, never a reply id (live_streaming_screen.dart:797).
      parentId: liveStreamingMessageId,
      // NO communityReply / mediaInfo / broadcastingInfo on a live send.
    };
  }
}
